import React, { FC } from "react";
import ReusableAccordian from "../ReusableAccordion";
import {
  Typography,
  Select,
  MenuItem,
  Paper,
  SelectChangeEvent,
  FormControlLabel,
  FormControl,
  Radio,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import useConfigSelection from "./useConfigSelection";

const StyledTypography = styled(Typography)({
  fontSize: 20,
  fontWeight: "bold",
  paddingRight: 20,
});

const StyledDivCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: " center",
  width: "100%",
  paddingTop: 20,
});

const StyledEmptyDiv = styled("div")({
  width: 300,
  height: 232,
  marginRight: 60,
});

const StyledPaper = styled(Paper)({
  marginRight: 60,
  backgroundColor: "white",
  padding: 10,
});

const StyledDivRow = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  alignItems: " center",
  width: "100%",
});

interface BifoldCornerConfigProps {
  hideHandlesWithEvenConfigs: (hide: boolean) => void;
}

const BifoldComboConfigAccordian: FC<BifoldCornerConfigProps> = ({
  hideHandlesWithEvenConfigs,
}) => {
  const {
    comboUnitLeft,
    comboUnitRight,
    getConfigVal,
    getChangeFn,
    getOptions,
    getImg,
    isPico,
    withExtSbolt,
    noExtAccess,
    handleChangeSboltExt,
  } = useConfigSelection(hideHandlesWithEvenConfigs);

  if (!comboUnitLeft || !comboUnitRight) {
    return (
      <ReusableAccordian
        accordionName="configuration"
        heading="Configuration"
        secondaryHeading="Select a configuration"
        required5={true}
        colOrRow="col"
      >
        <StyledDivRow>
          <Typography>Select a Combo unit placement</Typography>
        </StyledDivRow>
      </ReusableAccordian>
    );
  }

  const configLeft = getConfigVal(comboUnitLeft);
  const configRight = getConfigVal(comboUnitRight);

  return (
    <ReusableAccordian
      accordionName="configuration"
      heading="Configuration"
      secondaryHeading="Select a configuration"
      required5={true}
      colOrRow="col"
    >
      <RowLayout style={{ marginTop: -20 }}>
        <ComboOption
          config={configLeft}
          handleChange={getChangeFn(comboUnitLeft)}
          options={getOptions(comboUnitLeft)}
          img={getImg(comboUnitLeft)}
          label={comboUnitLeft}
        />

        <ComboOption
          config={configRight}
          handleChange={getChangeFn(comboUnitRight)}
          options={getOptions(comboUnitRight)}
          img={getImg(comboUnitRight)}
          label={comboUnitRight}
        />
      </RowLayout>

      {(isPico && noExtAccess) && (
          <StyledDivCol>
            <StyledDivCol style={{ minWidth: 170 }}>
              <Typography style={{ fontWeight: "bold" }}>
                Exterior shootbolt
                <span style={{ color: "red", fontSize: 22 }}>*</span>
              </Typography>
              <Typography style={{ fontWeight: "bold" }}>
                handle with key:
              </Typography>
            </StyledDivCol>

            <FormControl component="fieldset">
              <FormControlLabel
                value="end"
                control={
                  <Radio
                    color="primary"
                    checked={withExtSbolt === "No"}
                    onChange={handleChangeSboltExt}
                    value="No"
                  />
                }
                label="No"
                labelPlacement="end"
              />
              <FormControlLabel
                value="end"
                control={
                  <Radio
                    color="primary"
                    checked={withExtSbolt === "Yes"}
                    onChange={handleChangeSboltExt}
                    value="Yes"
                  />
                }
                label="Yes"
                labelPlacement="end"
              />
            </FormControl>
          </StyledDivCol>
        )}
    </ReusableAccordian>
  );
};

interface ComboOptionProps {
  config: string;
  handleChange: (event: SelectChangeEvent) => void;
  options: string[];
  img: string;
  label?: string;
}

const ComboOption: FC<ComboOptionProps> = ({
  config,
  handleChange,
  options,
  img,
  label = "",
}) => {
  return (
    <ColLayout style={{ height: 320 }}>
      <RowLayout style={{ marginBottom: 40 }}>
        <StyledTypography>{label} Configuration:</StyledTypography>
        <Select
          value={config}
          onChange={handleChange}
          variant="outlined"
          margin="dense"
          size="small"
          style={{ marginRight: 60, backgroundColor: "white" }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options?.map((itm) => (
            <MenuItem key={itm} value={itm.replace(" (Wider Panels)", "")}>
              {itm}
            </MenuItem>
          ))}
        </Select>
      </RowLayout>

      {img && config ? (
        <StyledPaper elevation={3}>
          <img src={img} alt="combo image" />
        </StyledPaper>
      ) : (
        <StyledEmptyDiv />
      )}
    </ColLayout>
  );
};

export default BifoldComboConfigAccordian;
